import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useProject } from "@/lib/workflow/useProject";
import { DOOR_MESSAGE, checkPass, doorTableLine, type DoorResult } from "@/lib/workflow/doorCheck";

/** Shown under a scanned FreeDailyPro pass: checks it against the open project on your device, and can check the guest in. */
export function DoorCheck({ code }: { code: string }) {
  const [project, store] = useProject();
  const [result, setResult] = useState<DoorResult | null>(null);
  useEffect(() => {
    let live = true;
    checkPass(code, project).then((r) => { if (live) setResult(r); }).catch(() => { if (live) setResult({ kind: "altered" }); });
    return () => { live = false; };
  }, [code, project]);

  if (!result || result.kind === "not-a-pass") return null;
  if (result.kind !== "valid") {
    return <div role="alert" data-testid="door-bad" className="p-3 rounded-lg border-2 border-destructive/50 bg-destructive/5 text-sm font-medium">{DOOR_MESSAGE[result.kind]}</div>;
  }
  const g = result.guest;
  const tone = result.alreadyIn || result.declined ? "border-amber-500/60 bg-amber-500/10" : "border-green-600/60 bg-green-600/10";
  return (
    <div data-testid="door-ok" className={`p-3 rounded-lg border-2 space-y-2 ${tone}`}>
      <p className="text-sm font-semibold">
        {result.alreadyIn ? "Already checked in" : "Valid pass"}: {g.name}{g.organisation ? `, ${g.organisation}` : ""}
      </p>
      {!result.declined && (doorTableLine(g)
        ? <p className="text-base font-bold" data-testid="door-table">{doorTableLine(g)}</p>
        : <p className="text-sm text-muted-foreground" data-testid="door-table">No table set yet</p>)}
      {g.role && <p className="text-xs text-muted-foreground">{g.role}</p>}
      {result.declined && <p className="text-sm">This guest replied that they are not coming.</p>}
      {!result.alreadyIn && (
        <Button size="sm" data-testid="door-checkin" onClick={() => { store.upsertRecords("guests", [{ ...g, status: "attended" }]); toast.success(`${g.name} checked in`); }}>
          Check in
        </Button>
      )}
    </div>
  );
}
